import { Button, Dialog } from "@mui/material";
import type { ReactNode } from "react";
import type { PhoneNumber } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { PhoneStep } from "./PhoneStep";
import { CodeStep } from "./CodeStep";
import { usePhoneVerification } from "./usePhoneVerification";

type Props = {
  isOpen: boolean;
  defaultPhone?: PhoneNumber;
  hasPhone: boolean;
  onClose?: () => void;
  description?: string;
  /**
   * The dialog's secondary action when it cannot be dismissed (no `onClose`) — the host decides
   * where a user who won't verify goes. With `onClose` set this is ignored in favour of Cancel.
   */
  secondaryAction?: ReactNode;
};

export const PhoneVerificationDialog = ({
  isOpen,
  defaultPhone,
  hasPhone,
  onClose,
  description,
  secondaryAction,
}: Props) => {
  const { t } = useResource();

  const verification = usePhoneVerification({
    defaultPhone,
    onVerified: onClose,
  });

  const resolvedSecondaryAction = onClose ? (
    <Button onClick={onClose}>{t(Resources.Common.Cancel)}</Button>
  ) : (
    secondaryAction
  );

  return (
    <Dialog
      open={isOpen}
      onClose={onClose ? () => onClose() : undefined}
      disableEscapeKeyDown={!onClose}
    >
      {verification.stage === "phoneEntry" ? (
        <PhoneStep
          hasPhone={hasPhone}
          isSending={verification.isSending}
          phoneForm={verification.phoneForm}
          onSubmit={verification.handleSendCode}
          description={description}
          secondaryAction={resolvedSecondaryAction}
        />
      ) : (
        <CodeStep
          submittedPhone={verification.submittedPhone}
          otpRef={verification.otpRef}
          isSending={verification.isSending}
          isVerifying={verification.isVerifying}
          isCodeComplete={verification.isCodeComplete}
          secondsLeft={verification.secondsLeft}
          isOnCooldown={verification.isOnCooldown}
          onCodeChange={verification.setCode}
          onVerify={verification.handleVerifyCode}
          onResend={verification.handleResendCode}
          onBackToPhone={verification.handleBackToPhone}
          secondaryAction={resolvedSecondaryAction}
        />
      )}
    </Dialog>
  );
};
